import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectADUCost,
  selectADUCosts,
  selectZipOnlyADUCost,
} from './costSelectors/selectADUCost.ts';
import {
  selectAtticCost,
  selectAtticCosts,
  selectZipOnlyAtticCost,
} from './costSelectors/selectAtticCost.ts';
import {
  selectBarnCost,
  selectBarnCosts,
  selectZipOnlyBarnCost,
} from './costSelectors/selectBarnCost.ts';
import {
  selectBelowGradeFullBathCost,
  selectBelowGradeFullBathCosts,
} from './costSelectors/selectBelowGradeFullBathCost.ts';
import {
  selectBelowGradeHalfBathCost,
  selectBelowGradeHalfBathCosts,
} from './costSelectors/selectBelowGradeHalfBathCost.ts';
import { selectCarportCost, selectCarportCosts } from './costSelectors/selectCarportCost.ts';
import {
  selectDeckCost,
  selectDeckCosts,
  selectZipOnlyDeckCost,
} from './costSelectors/selectDeckCost.ts';
import {
  selectDetachedGarageCost,
  selectDetachedGarageCosts,
} from './costSelectors/selectDetachedGarageCost.ts';
import {
  selectFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
} from './costSelectors/selectFinishedBasementAreaCost.ts';
import {
  selectFireplaceCost,
  selectFireplaceCosts,
  selectZipOnlyFireplaceCost,
} from './costSelectors/selectFireplaceCost.ts';
import { selectZipOnlyFoundationCost } from './costSelectors/selectFoundationCost.ts';
import { selectFullBathCost, selectFullBathCosts } from './costSelectors/selectFullBathCost.ts';
import { selectGarageCost, selectGarageCosts } from './costSelectors/selectGarageCost.ts';
import { selectHalfBathCost, selectHalfBathCosts } from './costSelectors/selectHalfBathCost.ts';
import {
  selectMainStructureCost,
  selectMainStructureCosts,
} from './costSelectors/selectMainStructureCost.ts';
import { selectManualCost, selectManualCosts } from './costSelectors/selectManualCost.ts';
import {
  selectPatioCost,
  selectPatioCosts,
  selectZipOnlyPatioCost,
} from './costSelectors/selectPatioCost.ts';
import {
  selectPoolCost,
  selectPoolCosts,
  selectZipOnlyPoolCost,
} from './costSelectors/selectPoolCost.ts';
import {
  selectPorchCost,
  selectPorchCosts,
  selectZipOnlyPorchCost,
} from './costSelectors/selectPorchCost.ts';
import {
  selectShedCost,
  selectShedCosts,
  selectZipOnlyShedCost,
} from './costSelectors/selectShedCost.ts';
import {
  selectSpaCost,
  selectSpaCosts,
  selectZipOnlySpaCost,
} from './costSelectors/selectSpaCost.ts';
import {
  selectTotalBasementAreaCost,
  selectTotalBasementAreaCosts,
} from './costSelectors/selectTotalBasementAreaCost.ts';
import {
  selectWorkshopCost,
  selectWorkshopCosts,
  selectZipOnlyWorkshopCost,
} from './costSelectors/selectWorkshopCost.ts';
import { selectImprovementEntityIds } from './propertyDataSelectors/improvementsSelectors.ts';

const calculateCostNew = (
  mainStructureCost?: ReturnType<typeof selectMainStructureCost>,
  garage?: ReturnType<typeof selectGarageCost>,
  detachedGarage?: ReturnType<typeof selectDetachedGarageCost>,
  carport?: ReturnType<typeof selectCarportCost>,
  basementFinished?: ReturnType<typeof selectFinishedBasementAreaCost>,
  basementTotal?: ReturnType<typeof selectTotalBasementAreaCost>,
  attic?: ReturnType<typeof selectAtticCost>,
  fireplace?: ReturnType<typeof selectFireplaceCost>,
  fullBaths?: ReturnType<typeof selectFullBathCost>,
  halfBaths?: ReturnType<typeof selectHalfBathCost>,
  belowGradeFullBaths?: ReturnType<typeof selectBelowGradeFullBathCost>,
  belowGradeHalfBaths?: ReturnType<typeof selectBelowGradeHalfBathCost>,
  shed?: ReturnType<typeof selectShedCost>,
  workshop?: ReturnType<typeof selectWorkshopCost>,
  barn?: ReturnType<typeof selectBarnCost>,
  pool?: ReturnType<typeof selectPoolCost>,
  spa?: ReturnType<typeof selectSpaCost>,
  adu?: ReturnType<typeof selectADUCost>,
  porch?: ReturnType<typeof selectPorchCost>,
  patio?: ReturnType<typeof selectPatioCost>,
  deck?: ReturnType<typeof selectDeckCost>,
  manualCost?: ReturnType<typeof selectManualCost>,
) => {
  if (!mainStructureCost) return undefined;

  return [
    mainStructureCost,
    garage,
    detachedGarage,
    carport,
    basementFinished,
    basementTotal,
    attic,
    fireplace,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    shed,
    workshop,
    barn,
    pool,
    spa,
    adu,
    porch,
    patio,
    deck,
    manualCost,
  ].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);
};

const calculateAdditionalStructuresCostNew = (
  attic?: ReturnType<typeof selectAtticCost>,
  fireplace?: ReturnType<typeof selectFireplaceCost>,
  shed?: ReturnType<typeof selectShedCost>,
  workshop?: ReturnType<typeof selectWorkshopCost>,
  barn?: ReturnType<typeof selectBarnCost>,
  pool?: ReturnType<typeof selectPoolCost>,
  spa?: ReturnType<typeof selectSpaCost>,
  adu?: ReturnType<typeof selectADUCost>,
  porch?: ReturnType<typeof selectPorchCost>,
  patio?: ReturnType<typeof selectPatioCost>,
  deck?: ReturnType<typeof selectDeckCost>,
  manualCost?: ReturnType<typeof selectManualCost>,
) =>
  AloftPrice.addWithNulls(
    attic,
    fireplace,
    shed,
    workshop,
    barn,
    pool,
    spa,
    adu,
    porch,
    patio,
    deck,
    manualCost,
  );

export const selectCostNew = createSelector(
  [
    selectMainStructureCost,
    selectGarageCost,
    selectDetachedGarageCost,
    selectCarportCost,
    selectFinishedBasementAreaCost,
    selectTotalBasementAreaCost,
    selectAtticCost,
    selectFireplaceCost,
    selectFullBathCost,
    selectHalfBathCost,
    selectBelowGradeFullBathCost,
    selectBelowGradeHalfBathCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateCostNew,
);

export const selectAdditionalStructureCostNew = createSelector(
  [
    selectAtticCost,
    selectFireplaceCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateAdditionalStructuresCostNew,
);

export const selectZipOnlyAdditionalStructureCostNew = createSelector(
  [
    selectZipOnlyAtticCost,
    selectZipOnlyFireplaceCost,
    selectZipOnlyShedCost,
    selectZipOnlyWorkshopCost,
    selectZipOnlyBarnCost,
    selectZipOnlyPoolCost,
    selectZipOnlySpaCost,
    selectZipOnlyADUCost,
    selectZipOnlyPorchCost,
    selectZipOnlyPatioCost,
    selectZipOnlyDeckCost,
    selectManualCost,
    selectZipOnlyFoundationCost,
  ],
  calculateAdditionalStructuresCostNew,
);

export const selectCostNews = createSelector(
  [
    selectMainStructureCosts,
    selectGarageCosts,
    selectDetachedGarageCosts,
    selectCarportCosts,
    selectFinishedBasementAreaCosts,
    selectTotalBasementAreaCosts,
    selectAtticCosts,
    selectFireplaceCosts,
    selectFullBathCosts,
    selectHalfBathCosts,
    selectBelowGradeFullBathCosts,
    selectBelowGradeHalfBathCosts,
    selectShedCosts,
    selectWorkshopCosts,
    selectBarnCosts,
    selectPoolCosts,
    selectSpaCosts,
    selectADUCosts,
    selectPorchCosts,
    selectPatioCosts,
    selectDeckCosts,
    selectManualCosts,
    selectImprovementEntityIds,
  ],
  (
    mainStructureCosts,
    garages,
    detachedGarages,
    carports,
    basementFinisheds,
    basementTotals,
    attics,
    fireplaces,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    sheds,
    workshops,
    barns,
    pools,
    spas,
    adus,
    porches,
    patios,
    decks,
    manualCosts,
    improvementEntityIds,
  ) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = calculateCostNew(
        mainStructureCosts[propID],
        garages[propID],
        detachedGarages[propID],
        carports[propID],
        basementFinisheds[propID],
        basementTotals[propID],
        attics[propID],
        fireplaces[propID],
        fullBaths[propID],
        halfBaths[propID],
        belowGradeFullBaths[propID],
        belowGradeHalfBaths[propID],
        sheds[propID],
        workshops[propID],
        barns[propID],
        pools[propID],
        spas[propID],
        adus[propID],
        porches[propID],
        patios[propID],
        decks[propID],
        manualCosts[propID],
      );

      if (isDefined(cost)) acc[propID] = cost;

      return acc;
    }, {}),
);

export const selectCostNewDisplay = createSelector([selectCostNew], (costNew) => {
  if (isUndefined(costNew)) {
    return undefined;
  }

  return costNew.toString(true);
});
